import React from 'react'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { get } from 'lodash'
import { useFormContext, useWatch } from 'react-hook-form'

import Flag from 'components/_base/Flag'
import FormAutoComplete from 'components/input/FormAutoComplete'
import FormSelect from 'components/input/FormSelect'
import FormTextField from 'components/input/FormTextField'
import DescriptionListTable from 'components/table/DescriptionListTable'
import { withForm } from 'decorators/withForm'
import { useCreateCompanyDataChangeRequest } from 'hooks/useDataChangeRequests'
import { companyDataToServer } from 'models/company-model'
import {
  countries,
  employeesOptions,
  employeesRanges,
  getEmployeesRange,
  linkedinIndustries,
  naces,
  normalizeCompanyChangeFormValues,
} from 'utils/data-change-requests'
import companyChangeRequest from 'validators/company-change-request'

const config = (isFieldDirty: any) => [
  {
    title: 'Name',
    render: () => <FormTextField name="name" />,
  },
  {
    title: 'Alias',
    field: 'alias',
  },
  {
    title: 'Number',
    field: 'number',
  },
  {
    title: 'UUID',
    field: 'uuid',
  },
  {
    title: 'Website',
    render: ({ website }: any) =>
      !website || isFieldDirty('website') ? (
        <FormTextField className="company-details__website-field" name="website" />
      ) : (
        website
      ),
  },
  {
    title: 'Country',
    render: (company: any) => <Flag country={company.country} />,
  },
  {
    title: 'City',
    render: () => <FormTextField name="address.city" />,
  },
  {
    title: 'Address',
    render: () => <FormTextField name="address.address" />,
  },
  {
    title: 'State',
    render: () => <FormTextField name="address.state" />,
  },
  {
    title: 'Zip Code',
    render: () => <FormTextField name="address.zipCode" />,
  },
  {
    title: 'Type',
    render: () => (
      <FormSelect
        allowEmpty
        name="address.type"
        options={[
          { value: 'hq', label: 'HQ' },
          { value: 'office', label: 'Office' },
        ]}
      />
    ),
  },
  {
    title: 'Phone country',
    render: () => (
      <FormAutoComplete label="Phone country" multiple={false} name="address.phoneNumber.country" options={countries} />
    ),
  },
  {
    title: 'Phone country code',
    render: () => <FormTextField name="address.phoneNumber.countryCode" type="number" />,
  },
  {
    title: 'Phone number',
    render: () => <FormTextField name="address.phoneNumber.number" type="number" />,
  },
  {
    title: 'Nace code (Industry)',
    render: () => <FormAutoComplete label="Nace code" multiple={false} name="naceCode" options={naces} />,
  },
  {
    title: 'LinkedIn Industry',
    render: () => (
      <FormAutoComplete
        label="LinkedIn industry"
        multiple={false}
        name="linkedInIndustryCode"
        options={linkedinIndustries}
      />
    ),
  },
  {
    title: 'Year founded',
    render: () => <FormTextField name="foundedAt" type="number" />,
  },
  {
    title: 'Description',
    render: () => <FormTextField multiline name="description" />,
  },
  {
    title: 'Financial Report year',
    render: () => <FormTextField name="financialReport.year" type="number" />,
  },
  {
    title: 'Financial Report currency',
    render: () => <FormTextField name="financialReport.currency" />,
  },
  {
    title: 'Financial Report revenue',
    render: () => <FormTextField name="financialReport.revenue" type="number" />,
  },
  {
    title: 'Tech',
    render: () => <FormTextField helperText="Enter comma-separated values!" name="tech" />,
  },
  {
    title: 'Employees year',
    render: () => <FormTextField name="employees.year" type="number" />,
  },
  {
    title: 'Employees',
    render: () => (
      <FormSelect
        allowEmpty={false}
        format={({ from, to }: any) => getEmployeesRange(from, to)}
        name="employees"
        normalize={(value: any, previousValue: any) => {
          return {
            ...previousValue,
            from: employeesRanges[value].from,
            to: employeesRanges[value].to,
          }
        }}
        options={employeesOptions}
      />
    ),
  },
  {
    title: 'LinkedIn Url',
    render: () => <FormTextField name="socials.linkedIn" />,
  },
  {
    title: 'Twitter Url',
    render: () => <FormTextField name="socials.twitter" />,
  },
  {
    title: 'Crunchbase Url',
    render: () => <FormTextField name="socials.crunchbase" />,
  },
  {
    title: 'Facebook Url',
    render: () => <FormTextField name="socials.facebook" />,
  },
]

type RequestChangeProps = {
  handleSubmit: (...args: any[]) => any
  initialValues: Record<string, any>
  open: boolean
  onClose: (...args: any[]) => any
  onSubmit: (...args: any[]) => any
  uuids?: any[]
}

const RequestChange = ({ handleSubmit, open, onClose, onSubmit, uuids }: RequestChangeProps) => {
  const { formState } = useFormContext()
  const formValues = useWatch()
  const createCompanyDataChangeRequest = useCreateCompanyDataChangeRequest()

  // Own RHF dirty state (was the isFieldDirty prop); drives payload + editable display.
  // Nested-leaf checks only work for leaf-registered fields (see dirtyFieldCheck for object fields).
  const isFieldDirty = (name: string) => !!get(formState.dirtyFields, name)

  const submitChangeRequest = async (values: any) => {
    const { uuid } = values
    const companiesUuids = (uuids?.length ?? 0) > 0 ? uuids : [uuid]

    const normalizedValues = normalizeCompanyChangeFormValues(values, isFieldDirty)
    await createCompanyDataChangeRequest.mutateAsync({
      companyUuids: companiesUuids,
      data: companyDataToServer(normalizedValues),
    })

    onSubmit(values)
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <form onSubmit={handleSubmit(submitChangeRequest)}>
        <DialogTitle>Request change</DialogTitle>
        <DialogContent>
          <DescriptionListTable config={config(isFieldDirty)} data={formValues} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button color="primary" type="submit">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default withForm({ form: 'requestChange', validate: companyChangeRequest })(RequestChange)
